/**
 * useAssetDeletion Hook
 *
 * Manages asset deletion operations including:
 * - Deleting assets
 * - Getting delete button visibility
 * - Toast notifications for deletion actions with undo
 * - Callback handling for asset deletion
 */

import { useCallback } from 'react'
import { deleteAsset, restoreAsset } from '../../../../assets/requests'
import { toast } from 'react-toastify'

export const useAssetDeletion = () => {
  const getAssetId = useCallback((state, props) => {
    let assetId = state?.currentAsset

    if (!assetId) {
      assetId =
        state?.modalData?.assetId ||
        Object.keys(props?.state?.publishedAssets || {})[0]
    }

    return assetId
  }, [])

  const deleteAssetById = useCallback(
    async (assetId, userId = null, onSuccess = null, onUndo = null) => {
      if (!assetId) {
        console.error('Asset ID is required to delete asset')
        toast.error('Unable to delete asset - missing asset ID', {
          autoClose: 2000,
          hideProgressBar: true
        })
        return false
      }

      const userIdToUse = userId || localStorage.getItem('username')

      try {
        await deleteAsset({
          userId: userIdToUse,
          assetId: assetId
        })

        if (onSuccess) {
          onSuccess(assetId)
        }

        const handleUndo = async () => {
          try {
            await restoreAsset({ userId: userIdToUse, assetId })
            if (onUndo) {
              onUndo(assetId)
            }
            toast.success('Photo restored!', {
              autoClose: 1000,
              hideProgressBar: true
            })
          } catch (err) {
            console.error('Failed to restore asset:', err)
            toast.error('Failed to restore photo.', {
              autoClose: 2000,
              hideProgressBar: true
            })
          }
        }

        toast.info(
          ({ closeToast }) => (
            <div>
              Photo deleted.{' '}
              <button
                onClick={() => { handleUndo(); closeToast(); }}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#3498db',
                  cursor: 'pointer',
                  textDecoration: 'underline',
                  fontSize: 'inherit',
                  padding: 0
                }}
              >
                Undo
              </button>
            </div>
          ),
          { autoClose: 5000, hideProgressBar: false }
        )

        return true
      } catch (error) {
        console.error('Failed to delete asset: ', error)
        toast.error('Failed to delete asset.', {
          autoClose: 2000,
          hideProgressBar: true
        })
        return false
      }
    },
    []
  )

  const handleDeleteAsset = useCallback(
    async (state, props, onClose) => {
      const assetId = getAssetId(state, props)

      // Capture the asset data before deletion so we can restore it on undo
      const deletedAssetData = state.publishedAssets?.[assetId] || null

      const onSuccess = deletedAssetId => {
        if (props.onAssetDeleted) {
          props.onAssetDeleted(deletedAssetId)
        }

        // Remove asset from all asset stores (raw, signed, published)
        if (props.dispatch) {
          props.dispatch({ type: 'remove_asset', payload: deletedAssetId })
        }

        if (onClose) {
          onClose()
        }
      }

      const onUndo = restoredAssetId => {
        // Re-add asset to published assets in state
        if (props.dispatch && deletedAssetData) {
          props.dispatch({
            type: 'append_published_assets',
            payload: { [restoredAssetId]: deletedAssetData }
          })
        }
      }

      return await deleteAssetById(assetId, null, onSuccess, onUndo)
    },
    [getAssetId, deleteAssetById]
  )

  const getDeleteButtonVisibility = useCallback(() => {
    return true
  }, [])

  return {
    deleteAssetById,
    handleDeleteAsset,
    getDeleteButtonVisibility,
    getAssetId
  }
}

export default useAssetDeletion
